/**
 * Load a patient's imported generated-profile documents + their divergences.
 *
 * A profile is an ordinary `type=markdown` document distinguished by a provenance
 * record; it is visible on the Blueprint surface but display-only (its claims are
 * never committed to the card graph). This hook lists a patient's profiles
 * (`GET /api/documents/profiles`) and, on demand, loads the document-level
 * divergence for one (`GET /api/documents/{id}/profile-divergence`) so the panel
 * can show what a newer generated version changed — never overwriting an edit.
 *
 * No PHI is logged: profile content is fetched and rendered but never logged.
 */
import { useCallback, useEffect, useState } from "react";
import apiUtils, { isApiError } from "../apiUtils";
import logger from "../utils/logger";
import type { ProfileDivergenceResponse, ProfileSummary } from "../types";

const DOCUMENTS_ENDPOINT = "/api/documents";

/** The load state of the profile list. */
export type ProfilesStatus = "loading" | "loaded" | "error";

/** The value returned by {@link useProfiles}. */
export interface UseProfilesResult {
  /** The patient's imported profiles (empty until loaded, or on error). */
  profiles: ProfileSummary[];
  /** The list load state. */
  status: ProfilesStatus;
  /** Re-fetch the profile list. */
  reload: () => void;
  /**
   * Load the document-level divergence for one profile document, or null on
   * error (the panel degrades to no diff rather than surfacing a raw failure).
   */
  loadDivergence: (
    documentId: string,
  ) => Promise<ProfileDivergenceResponse | null>;
}

/**
 * Manage a patient's imported profiles for the Blueprint-surface profile panel.
 *
 * @param patientId - The active patient id.
 * @returns The profiles, load status, and reload/divergence loaders.
 */
export function useProfiles(patientId: string): UseProfilesResult {
  const [profiles, setProfiles] = useState<ProfileSummary[]>([]);
  const [status, setStatus] = useState<ProfilesStatus>("loading");
  const [token, setToken] = useState(0);

  const reload = useCallback(() => setToken((t) => t + 1), []);

  useEffect(() => {
    let active = true;

    async function load(): Promise<void> {
      setStatus("loading");
      const res = await apiUtils.fetchData<ProfileSummary[]>(
        `${DOCUMENTS_ENDPOINT}/profiles?patient_id=${encodeURIComponent(patientId)}`,
      );
      if (!active) return;
      if (isApiError(res)) {
        setProfiles([]);
        setStatus("error");
        return;
      }
      setProfiles(res);
      setStatus("loaded");
    }

    load().catch((err: unknown) => {
      if (!active) return;
      logger.devOnly.warn("useProfiles: load failed", { error: String(err) });
      setProfiles([]);
      setStatus("error");
    });

    return () => {
      active = false;
    };
  }, [patientId, token]);

  const loadDivergence = useCallback(
    async (documentId: string): Promise<ProfileDivergenceResponse | null> => {
      const res = await apiUtils.fetchData<ProfileDivergenceResponse>(
        `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}/profile-divergence`,
      );
      return isApiError(res) ? null : res;
    },
    [],
  );

  return { profiles, status, reload, loadDivergence };
}

export default useProfiles;
